/**
 * サインイン応答の意味づけ（純粋関数）。
 *
 * ## なぜ分けるか（2026-09-07・パスワード再設定してもログインできない調査）
 *
 * ログイン画面は 429 以外の全失敗を「メールアドレスまたはパスワードが正しくありません」に
 * 潰していた。そのため次の3つが利用者にも管理者にも同じに見え、事後に切り分けられなかった:
 *
 *  - 401 … パスワード不一致 / ユーザー不在 / credential 行が3条件に合わない
 *  - 403 … 退職者ガードの遮断・ガード障害（lib/auth/auth.ts の databaseHooks.session.create.before）。
 *          **パスワードの照合は通っている**（better-auth 1.7.2 dist/api/routes/sign-in.mjs:334-355 で
 *          password.verify の後に createSession を呼ぶ）。つまり「再設定は成功したのに
 *          パスワードが違うと言われる」という申告が、この経路だけで字義通り成立する。
 *          サーバは利用者に見せて安全な日本語メッセージを返している（SIGN_IN_BLOCK_MESSAGES）
 *  - 2FA  … twoFactor プラグインは credential サインインが作ったセッションを消して
 *          `{ twoFactorRedirect: true, twoFactorMethods }` を返す
 *          （better-auth 1.7.2 dist/plugins/two-factor/index.mjs:325-327）。
 *          `data.user` が無いため、旧実装は「ログインに失敗しました」に落ちて
 *          /two-factor へ遷移しなかった＝2FA 利用者は行き止まりになる
 *
 * 遮断条件そのもの（誰を止めるか）はここでは一切変えない。**見せ方だけ**を分ける。
 */

/** 2FA の手段が1つも提示されない場合に出す案内。 */
export const NO_TWO_FACTOR_METHOD_MESSAGE =
  '2段階認証が有効ですが、利用できる認証手段が登録されていません。管理者にお問い合わせください。'

/** 401 系（原因を利用者へ明かさない・列挙対策）の統一文言。 */
export const INVALID_CREDENTIALS_MESSAGE = 'メールアドレスまたはパスワードが正しくありません'

export type SignInOutcome =
  | { kind: 'rate-limited' }
  | { kind: 'blocked'; message: string }
  | { kind: 'invalid-credentials' }
  | { kind: 'two-factor'; hasMethod: boolean }
  | { kind: 'authenticated'; email: string }
  | { kind: 'unknown' }

interface SignInErrorLike {
  status?: number
  message?: string
}

interface SignInDataLike {
  user?: { email?: unknown } | null
  twoFactorRedirect?: unknown
  twoFactorMethods?: unknown
}

/**
 * better-auth の `{ data, error }` を、画面が分岐できる形へ写す。
 *
 * @returns 'blocked' の message はサーバが返した文言をそのまま使う
 *   （SIGN_IN_BLOCK_MESSAGES は利用者向けに書かれており、内部情報を含まない）。
 */
export function resolveSignInOutcome(
  data: SignInDataLike | null | undefined,
  error: SignInErrorLike | null | undefined
): SignInOutcome {
  if (error) {
    if (error.status === 429) return { kind: 'rate-limited' }
    // 403 は「パスワードは合っていたが、別の理由で止めた」。潰すと原因が消える。
    if (error.status === 403 && typeof error.message === 'string' && error.message.trim() !== '') {
      return { kind: 'blocked', message: error.message }
    }
    return { kind: 'invalid-credentials' }
  }

  if (data?.twoFactorRedirect) {
    const methods = Array.isArray(data.twoFactorMethods) ? data.twoFactorMethods : []
    return { kind: 'two-factor', hasMethod: methods.length > 0 }
  }

  const email = data?.user?.email
  // プロフィール取得は **入力値ではなく DB の保存値** で行う。
  // 入力の大小が保存値と違うと /api/auth/profile が 403 を返し、
  // サインイン自体は成功しているのに画面が進まない（cookie は発行済み）。
  if (typeof email === 'string' && email !== '') return { kind: 'authenticated', email }

  return { kind: 'unknown' }
}
